import { useEffect, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { RequestStatus, useBFFDataProvider } from '@atlassian/navigation-bff-data-provider';

import type { ShortcutsConfig } from '../../types';
import { productLinksWorkspaceCloudIdFilter } from '../product-link-filters';

import type { UseGlobalShortcutDataInput, UseGlobalShortcutDataOutput } from './types';

/**
 *
 * @param param0 - shortcutsConfig: Configuration for global shortcuts.
 * @returns app shortcuts config with URLs hydrated from the BFF data provider.
 */
export const useGlobalShortcutsData = ({
	shortcutsConfig,
	cloudId,
	orgId,
	isShortcutsStatic,
}: UseGlobalShortcutDataInput): UseGlobalShortcutDataOutput => {
	const { buildURLAndFetchData, status, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		orgId,
		forceStatic: isShortcutsStatic,
	});

	// fetch the data from the bff if required
	useEffect(() => {
		if (isEmpty(shortcutsConfig) || isShortcutsStatic) {
			return;
		}
		const apsKeys = Object.values(shortcutsConfig)
			.map((item) => item.apsKey || '')
			.filter((item) => !!item);
		buildURLAndFetchData(apsKeys);
	}, [buildURLAndFetchData, shortcutsConfig, isShortcutsStatic]);

	// associate shortcuts with the returned bff data
	const hydratedAppShortcuts = useMemo(() => {
		if (status === RequestStatus.COMPLETE && Object.keys(productLinks).length) {
			const entries = Object.entries(shortcutsConfig || {});

			return entries.reduce<ShortcutsConfig>((acc, [key, value]) => {
				// if config does not need a data reference, just return the value
				if (!value.apsKey) {
					acc[key] = value;
					return acc;
				}
				// if config requires the bff to determine the data reference
				const productLink = productLinks[value.apsKey];
				if (productLink) {
					const filteredProductLink = cloudId
						? productLink.filter(productLinksWorkspaceCloudIdFilter(cloudId))[0]
						: productLink[0];
					acc[key] = {
						...value,
						url: filteredProductLink.url,
					};
					return acc;
				}
				return acc;
			}, {});
		}

		return shortcutsConfig;
	}, [productLinks, status, shortcutsConfig, cloudId]);

	return hydratedAppShortcuts;
};
